import { Spinner2 } from 'components';
import { useTranslation } from 'react-i18next';
import { ERROR_CATEGORY_LABELS, fmtDate, httpCodeColor, statusBadge } from './helpers';

const LogTable = ({ rows, loading, onFilterByPlan }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner2 />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-xl bg-[#F0F2F4] flex items-center justify-center mb-4">
          <svg
            className="w-5 h-5 text-[#B5BDCA]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <p className="text-[13px] text-[#97A3B5]">{t('admin.reporting.webhooks.empty')}</p>
      </div>
    );
  }

  const COLS = [
    t('admin.reporting.webhooks.cols.subscriptionPlan', { defaultValue: 'Subscription Plan' }),
    t('admin.reporting.webhooks.cols.subscriptionPlanId', { defaultValue: 'Plan ID' }),
    t('admin.reporting.webhooks.cols.eventId'),
    t('admin.reporting.webhooks.cols.eventType'),
    t('admin.reporting.webhooks.cols.endpoint'),
    t('admin.reporting.webhooks.cols.timestamp'),
    t('admin.reporting.webhooks.cols.status'),
    t('admin.reporting.webhooks.cols.httpCode'),
    t('admin.reporting.webhooks.cols.retries'),
    t('admin.reporting.webhooks.cols.duration'),
    t('admin.reporting.webhooks.cols.errorCategory'),
  ];

  // Pre-compute row spans — group consecutive rows that share the same non-null subscriptionPlanId
  const groupSpans = rows.map((row, i) => {
    if (!row.subscriptionPlanId) return 1;
    if (i > 0 && rows[i - 1].subscriptionPlanId && row.subscriptionPlanId === rows[i - 1].subscriptionPlanId) return 0;
    let span = 1;
    while (i + span < rows.length && rows[i + span].subscriptionPlanId === row.subscriptionPlanId) span++;
    return span;
  });

  return (
    <div className="rounded-xl border border-[#E5E7EB] overflow-x-auto">
      <table className="w-full text-left border-collapse min-w-[800px]">
        <thead>
          <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
            {COLS.map((col) => (
              <th
                key={col}
                className="px-3 py-2.5 text-[10px] font-semibold text-[#7989A0] uppercase tracking-wide whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.eventId}
              className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] transition-colors ${
                i % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white'
              }`}
            >
              {/* Subscription Plan — grouped via rowSpan */}
              {groupSpans[i] !== 0 && (
                <td
                  rowSpan={groupSpans[i]}
                  className="px-3 py-2.5 whitespace-nowrap align-middle border-r border-[#E5E7EB]"
                >
                  {row.subscriptionPlanName ? (
                    <button
                      type="button"
                      onClick={() => onFilterByPlan && onFilterByPlan(row.subscriptionPlanName)}
                      className="text-[11px] font-medium text-[#334155] bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2 py-0.5 hover:bg-[#E2E8F0] transition-colors cursor-pointer"
                      title={`Filter by: ${row.subscriptionPlanName}`}
                    >
                      {row.subscriptionPlanName}
                    </button>
                  ) : (
                    <span className="text-[11px] text-[#94A3B8]">—</span>
                  )}
                </td>
              )}

              {/* Plan ID — grouped same as Subscription Plan */}
              {groupSpans[i] !== 0 && (
                <td
                  rowSpan={groupSpans[i]}
                  className="px-3 py-2.5 whitespace-nowrap align-middle border-r border-[#E5E7EB]"
                >
                  {row.subscriptionPlanId ? (
                    <span className="font-mono text-[11px] text-[#475569] bg-[#F1F5F9] rounded px-1.5 py-0.5">
                      {row.subscriptionPlanId}
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#94A3B8]">—</span>
                  )}
                </td>
              )}

              {/* Event ID */}
              <td className="px-3 py-2.5 text-[11px] font-mono text-[#475569] whitespace-nowrap">
                <span className="bg-[#F1F5F9] rounded px-1.5 py-0.5">{row.eventId}</span>
              </td>

              {/* Event Type */}
              <td className="px-3 py-2.5 text-[12px] text-[#353E4A] whitespace-nowrap font-medium">
                {row.eventType}
              </td>

              {/* Endpoint */}
              <td
                className="px-3 py-2.5 text-[11px] text-[#64748B] max-w-[180px] truncate"
                title={row.webhookEndpoint}
              >
                {row.webhookEndpoint ?? '—'}
              </td>

              {/* Time (UTC) */}
              <td className="px-3 py-2.5 text-[11px] text-[#64748B] whitespace-nowrap">
                {fmtDate(row.receivedAt)}
              </td>

              {/* Status */}
              <td className="px-3 py-2.5 whitespace-nowrap">
                <span
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${statusBadge(
                    row.status
                  )}`}
                >
                  {t(`admin.reporting.webhooks.status.${row.status}`, { defaultValue: row.status })}
                </span>
              </td>

              {/* HTTP Code */}
              <td
                className={`px-3 py-2.5 text-[12px] font-semibold whitespace-nowrap ${httpCodeColor(
                  row.httpStatusCode
                )}`}
              >
                {row.httpStatusCode ?? '—'}
              </td>

              {/* Retry Count */}
              <td className="px-3 py-2.5 text-[12px] text-center text-[#475569]">
                {row.retryCount ?? 0}
              </td>

              {/* Duration */}
              <td className="px-3 py-2.5 text-[12px] text-[#64748B] whitespace-nowrap">
                {row.durationMs != null ? `${row.durationMs} ms` : '—'}
              </td>

              {/* Error Category */}
              <td className="px-3 py-2.5 whitespace-nowrap">
                {row.errorCategory ? (
                  <span className="text-[11px] font-medium text-[#EA580C] bg-[#FFF7ED] border border-[#FED7AA] rounded-full px-2 py-0.5">
                    {ERROR_CATEGORY_LABELS[row.errorCategory] ?? row.errorCategory}
                  </span>
                ) : (
                  <span className="text-[11px] text-[#94A3B8]">—</span>
                )}
              </td>

            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LogTable;
